<template>
    <header class="header">
        <!-- Search Overlay -->
        <div v-if="isSearchOpen && !isSearchExpanded" class="search-overlay">
            <div class="search-modal">
                <div class="search-header">
                    <h3 class="search-title">Search</h3>
                    <button @click="toggleSearch" class="close-btn" aria-label="Close search">
                        <CloseIcon class="w-6 h-6" />
                    </button>
                </div>
                <div class="search-input-wrapper">
                    <input type="text" v-model="searchText" placeholder="Search for products, solutions, etc..." aria-label="Search input" />
                    <button aria-label="Submit search">
                        <SearchIcon class="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Desktop Header (both normal and fixed) -->
        <div class="desktop-header" :class="{ 'is-fixed': showFixedHeader, 'is-hidden': !showFixedHeader && showFixedHeader !== false }">
            <div class="header-inner">
                <!-- Logo -->
                <div class="logo">
                    <nuxt-link to="/">
                        <img src="https://images.leaflypackaging.com/profile/upload/2026/01/29/logo_20260129164152A002.webp" alt="Leafly Packaging" />
                    </nuxt-link>
                </div>

                <!-- Navigation -->
                <nav class="main-nav">
                    <div v-for="(navItem, index) in navItemList" :key="index" class="nav-item" :class="{ 'active': navItem.active }">
                        <nuxt-link :to="navItem.linkUrl" class="nav-link">
                            {{ navItem.name }}
                            <ChevronDownIcon v-if="navItem.children?.length" class="arrow-icon" />
                        </nuxt-link>

                        <!-- Submenu -->
                        <div v-if="navItem.children?.length" class="submenu">
                            <nuxt-link v-for="(subItem, subIndex) in navItem.children" :key="subIndex" :to="subItem.linkUrl" class="submenu-link">
                                {{ subItem.name }}
                            </nuxt-link>
                        </div>
                    </div>
                </nav>

                <!-- Right Actions -->
                <div class="header-actions">
                    <div class="search-wrapper">
                        <div class="search-input-box" :class="{ 'expanded': isSearchExpanded }">
                            <input ref="searchInput" type="text" v-model="searchText" placeholder="Search..." aria-label="Search products" />
                        </div>
                        <button @click="toggleSearchInput" class="search-toggle" aria-label="Toggle search">
                            <SearchIcon class="w-5 h-5" />
                        </button>
                    </div>
                    <nuxt-link to="/contact-us" class="contact-btn">CONTACT US</nuxt-link>
                </div>
            </div>
        </div>

        <!-- Mobile Header -->
        <div class="mobile-header">
            <div class="mobile-header-inner">
                <div class="logo">
                    <nuxt-link to="/">
                        <img src="https://images.leaflypackaging.com/profile/upload/2026/01/29/logo_20260129164152A002.webp" alt="Leafly Packaging" />
                    </nuxt-link>
                </div>

                <!-- Mobile Search Expanded -->
                <div v-if="isSearchExpanded" class="mobile-search-expanded">
                    <div class="mobile-search-header">
                        <h3>Search</h3>
                        <button @click="toggleSearchInput" aria-label="Close search">
                            <CloseIcon class="w-6 h-6" />
                        </button>
                    </div>
                    <div class="search-input-wrapper">
                        <input type="text" v-model="searchText" placeholder="Search..." aria-label="Search input" />
                        <button aria-label="Submit search">
                            <SearchIcon class="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <!-- Mobile Actions -->
                <div class="mobile-actions">
                    <button @click="toggleSearchInput" class="search-toggle-mobile" aria-label="Open search">
                        <SearchIcon class="w-5 h-5" />
                    </button>
                    <button @click="toggleMobileMenu" class="menu-toggle" :aria-label="mobileMenuOpen ? 'Close menu' : 'Open menu'">
                        <MenuIcon v-if="!mobileMenuOpen" class="w-6 h-6" />
                        <CloseIcon v-else class="w-6 h-6" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Mobile Menu -->
        <div v-show="mobileMenuOpen" class="mobile-menu">
            <div v-for="(navItem, index) in navItemList" :key="index" class="mobile-nav-item">
                <div @click="toggleMobileSubmenu(index)" class="mobile-nav-link">
                    <nuxt-link v-if="!navItem.children?.length" :to="navItem.linkUrl" class="full-width">{{ navItem.name }}</nuxt-link>
                    <span v-else>{{ navItem.name }}</span>
                    <ChevronDownIcon v-if="navItem.children?.length" class="mobile-arrow" :class="{ 'rotated': expandedMenus.includes(index) }" />
                </div>
                <div v-if="navItem.children?.length && expandedMenus.includes(index)" class="mobile-submenu">
                    <nuxt-link v-for="(subItem, subIndex) in navItem.children" :key="subIndex" :to="subItem.linkUrl" class="mobile-submenu-link">
                        {{ subItem.name }}
                    </nuxt-link>
                </div>
            </div>
            <div class="mobile-contact">
                <nuxt-link to="/contact-us" class="mobile-contact-btn">Contact Us</nuxt-link>
            </div>
        </div>
    </header>
</template>

<script setup lang="ts">
    import cmsConfig from '~/cmsConfig';
    import cmsUtils from '~/utils/cmsUtils';
    import type { NavItem } from '~/types';
    import { ref, onMounted, onUnmounted } from 'vue';
    import { isEmpty } from '~/utils/serverApiAndNuxtApi/nuxtCore';
    import SearchIcon from '../icons/SearchIcon.vue';
    import CloseIcon from '../icons/CloseIcon.vue';
    import MenuIcon from '../icons/MenuIcon.vue';
    import ChevronDownIcon from '../icons/ChevronDownIcon.vue';

    // SEO 配置
    useHead({
        htmlAttrs: {
            lang: 'en'
        },
        meta: [
            { name: 'locale', content: 'en_US' },
            { name: 'robots', content:"index, follow"}
        ],
    })

    const isSearchOpen = ref(false)
    const isSearchExpanded = ref(false)
    const searchText = ref('')
    const activeNvItem = ref(0)
    const searchInput = ref<HTMLInputElement | null>(null)
    const searchInputFixed = ref<HTMLInputElement | null>(null)
    const mobileMenuOpen = ref(false)
    const expandedMenus = ref<number[]>([])
    const cmsInfo = await cmsUtils.getCmsInfo()
    const showFixedHeader = ref(false)

    function toggleSearch() {
        isSearchOpen.value = !isSearchOpen.value
    }

    function toggleSearchInput() {
        isSearchExpanded.value = !isSearchExpanded.value

        if (isSearchExpanded.value) {
            setTimeout(() => {
                if (showFixedHeader.value) {
                    searchInputFixed.value?.focus()
                } else {
                    searchInput.value?.focus()
                }
            }, 300)
        } else {
            if(!isEmpty(searchText.value)){
                navigateTo(`/search/${searchText.value}.html`)
            }
            searchText.value = ''
        }
    }

    function toggleMobileMenu() {
        mobileMenuOpen.value = !mobileMenuOpen.value
    }

    function toggleMobileSubmenu(index: number) {
        const position = expandedMenus.value.indexOf(index)
        if (position > -1) {
            expandedMenus.value.splice(position, 1)
        } else {
            expandedMenus.value.push(index)
        }
    }

    function handleScroll() {
        if (window.scrollY > 100) {
            showFixedHeader.value = true
        } else {
            showFixedHeader.value = false
        }
    }

    onMounted(() => {
        window.addEventListener('scroll', handleScroll)
    })

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll)
    })

    const navItemList = ref<NavItem[]>(cmsUtils.getNavItemTreeByNavIndex("header_main", cmsInfo))
    const routerPath = useRoute().path

    let routePathItemStr = routerPath.replace('.html', '').split("/")[1]

    navItemList.value.forEach(item => {
        if (!item.linkUrl) {
            item.active = false;
            return
        }

        if((routerPath.includes('/packaging/') ||routerPath.includes('/packaging'))  && item.linkUrl?.includes('violet-jar')){
            item.active = false
            return;
        }

        if (item.linkUrl == '/' && routerPath !== '/') {
            item.active = false;
            return
        }

        if (routePathItemStr === item.linkUrl!.replace('.html', '').split("/")[1]) {
            item.active = true
            return
        }
    })

</script>

<style scoped lang='scss'>
    .header {
        width: 100%;
        position: relative;
        top: 0;
        left: 0;
        z-index: 1000;
        background-color: var(--white);
        border-bottom: 1px solid var(--gray-100);
    }

    // Search Overlay
    .search-overlay {
        position: fixed;
        inset: 0;
        background-color: rgba(255, 255, 255, 0.98);
        z-index: 50;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
    }

    .search-modal {
        width: 100%;
        max-width: 32rem;
        height: 4.875rem;
    }

    .search-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1.5rem;
    }

    .search-title {
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--gray-700);
    }

    .close-btn {
        color: var(--gray-500);
        background: none;
        border: none;
        cursor: pointer;
        padding: 0.25rem;

        &:hover {
            color: var(--gray-800);
        }

        svg {
            width: 1.5rem;
            height: 1.5rem;
        }
    }

    .search-input-wrapper {
        position: relative;

        input {
            width: 100%;
            padding: 0.75rem 1rem;
            padding-right: 3.5rem;
            border: 2px solid var(--green-600);
            border-radius: 9999px 0 0 9999px;
            outline: none;
            color: var(--gray-700);
            font-size: 1rem;

            &:focus {
                border-color: var(--green-600);
                box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.2);
            }
        }

        button {
            position: absolute;
            right: 0;
            top: 0;
            height: 100%;
            background-color: var(--green-600);
            color: var(--white);
            padding: 0.5rem 1rem;
            border-radius: 0 9999px 9999px 0;
            border: none;
            cursor: pointer;

            &:hover {
                background-color: var(--green-700);
            }

            svg {
                width: 1rem;
                height: 1rem;
            }
        }
    }

    // Desktop Header
    .desktop-header {
        display: none;

        @media (min-width: 1024px) {
            display: block;
        }

        &.is-fixed {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            background-color: var(--white);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            z-index: 40;
            transform: translateY(0);
            transition: transform 0.3s;

            &.is-hidden {
                transform: translateY(-100%);
            }
        }
    }

    .header-inner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 0 1.5rem;
        height: 6.125rem;
    }

    .logo {
        a {
            display: flex;
            align-items: center;
        }

        img {
            height: 3.75rem;
            width: 100%;
        }
    }

    // Navigation
    .main-nav {
        display: flex;
        justify-content: center;
        flex: 1;
        height: 100%;
    }

    .nav-item {
        position: relative;
        display: flex;
        align-items: center;
        padding: 0 1.25rem;

        &:hover {
            .submenu {
                display: block;
            }
        }

        &.active {
            .nav-link {
                color: var(--green-600);

                &::after {
                    left: 0;
                    right: 0;
                    opacity: 1;
                }
            }
        }
    }

    .nav-link {
        display: flex;
        align-items: center;
        color: var(--gray-800);
        font-size: 1rem;
        font-weight: 600;
        text-transform: uppercase;
        text-decoration: none;
        position: relative;

        &:hover {
            color: var(--green-600);

            &::after {
                left: 0;
                right: 0;
                opacity: 1;
            }
        }

        &::after {
            content: '';
            position: absolute;
            bottom: -39px;
            left: 50%;
            right: 50%;
            height: 4px;
            background-color: var(--green-500);
            transition: all 0.3s ease;
            opacity: 0;
            margin-bottom: 2px;
        }

        .arrow-icon {
            width: 0.75rem;
            height: 0.75rem;
            margin-left: 0.375rem;
        }
    }

    .submenu {
        position: absolute;
        top: 100%;
        left: 0;
        background-color: var(--white);
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        border-top: 1px solid var(--gray-100);
        padding: 0.75rem 0;
        width: 16rem;
        z-index: 50;
        display: none;

        .submenu-link {
            display: block;
            padding: 0.625rem 1.25rem;
            font-size: 1rem;
            font-weight: 600;
            color: var(--gray-700);
            text-decoration: none;

            &:hover {
                background-color: var(--green-50);
                color: var(--green-600);
            }
        }
    }

    // Header Actions
    .header-actions {
        display: flex;
        align-items: center;
    }

    .search-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        margin-right: 0.75rem;
    }

    .search-input-box {
        position: absolute;
        right: 0;
        display: flex;
        align-items: center;
        background-color: var(--white);
        overflow: hidden;
        margin-right: 0.25rem;
        transition: all 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55);
        max-width: 0;
        opacity: 0;
        transform: translateX(20px);

        &.expanded {
            max-width: 18.75rem;
            width: 15.625rem;
            opacity: 1;
            transform: translateX(0);
        }

        input {
            width: 100%;
            padding: 0.5rem 1rem;
            border: 2px solid var(--green-600);
            border-radius: 9999px;
            outline: none;
            color: var(--gray-700);

            &:focus {
                border-color: var(--green-600);
            }
        }
    }

    .search-toggle {
        background-color: var(--green-600);
        color: var(--white);
        border: none;
        padding: 0.625rem;
        border-radius: 9999px;
        cursor: pointer;
        z-index: 10;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: border-radius 0.2s;

        &:hover {
            background-color: var(--green-700);
        }

        svg {
            width: 1.25rem;
            height: 1.25rem;
        }
    }

    .search-input-box.expanded + .search-toggle {
        border-radius: 0 9999px 9999px 0;
    }

    .contact-btn {
        background-color: var(--green-600);
        color: var(--white);
        font-weight: 600;
        padding: 0.625rem 1.5rem;
        border-radius: 0.25rem;
        text-transform: uppercase;
        text-decoration: none;
        transition: background-color 0.2s;

        &:hover {
            background-color: var(--green-700);
        }
    }

    // Mobile Header
    .mobile-header {
        display: none;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid var(--gray-100);

        @media (max-width: 1023px) {
            display: block;
        }
    }

    .mobile-header-inner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    .mobile-header .logo img {
        height: 2.5rem;
        width: 100%;
    }

    .mobile-search-expanded {
        position: fixed;
        inset: 0;
        background-color: rgba(255, 255, 255, 0.95);
        z-index: 50;
        display: flex;
        flex-direction: column;
        padding: 1rem;
    }

    .mobile-search-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;

        h3 {
            font-weight: 600;
            font-size: 1.125rem;
        }
    }

    .mobile-actions {
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .search-toggle-mobile {
        background-color: var(--green-600);
        color: var(--white);
        border: none;
        padding: 0.5rem;
        border-radius: 9999px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
            width: 1.25rem;
            height: 1.25rem;
        }
    }

    .menu-toggle {
        color: var(--gray-600);
        background: none;
        border: none;
        cursor: pointer;

        svg {
            width: 1.5rem;
            height: 1.5rem;
        }
    }

    // Mobile Menu
    .mobile-menu {
        display: none;
        padding: 0.5rem 1.5rem;
        border-bottom: 1px solid var(--gray-100);
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

        @media (max-width: 1023px) {
            display: block;
        }
    }

    .mobile-nav-item {
        border-bottom: 1px solid var(--gray-100);

        &:last-child {
            border-bottom: none;
        }
    }

    .mobile-nav-link {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        color: var(--gray-800);
        text-transform: uppercase;
        font-size: 0.875rem;
        padding: 0.875rem 0;

        &:hover {
            color: var(--green-600);
        }

        .full-width {
            display: block;
            width: 100%;
            text-decoration: none;
        }
    }

    .mobile-arrow {
        width: 1rem;
        height: 1rem;
        transition: transform 0.3s;

        &.rotated {
            transform: rotate(180deg);
        }
    }

    .mobile-submenu {
        padding-left: 1rem;
        padding-top: 0.25rem;
        padding-bottom: 0.25rem;
        background-color: var(--gray-50);
    }

    .mobile-submenu-link {
        display: block;
        padding: 0.625rem 0;
        font-size: 0.875rem;
        color: var(--gray-700);
        text-decoration: none;
        border-top: 1px solid var(--gray-100);

        &:first-child {
            border-top: none;
        }

        &:hover {
            color: var(--green-600);
        }
    }

    .mobile-contact {
        margin-top: 1.25rem;
        padding-bottom: 0.75rem;
    }

    .mobile-contact-btn {
        display: block;
        width: 100%;
        padding: 0.625rem 1rem;
        text-align: center;
        background-color: var(--green-600);
        color: var(--white);
        border-radius: 0.25rem;
        text-transform: uppercase;
        font-weight: 600;
        text-decoration: none;
    }
</style>
